import './Table.scss';
import { useSortableTableData } from './hooks/useSortableTableData';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTableSearch } from './hooks/useTableSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { inject } from 'mobx-react';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import DroppableRow from '../droppable-row/droppable-row';
import DraggableRow from '../draggable-row/draggable-row';
import PropTypes from 'prop-types';
import Checkbox from '../checkbox';
import LoadingSpinner from '../loading-spinner/loading-spinner';

const Table = ({
  data,
  columnNames,
  tableData,
  searchable = true,
  searchPlaceholder,
  sortable = true,
  isLoading = false,
  onRowClick = () => {},
  onExpenseDrop = () => {},
  selectableRows = false,
  selectedRows = null,
  selectableRowsCondition = () => true,
  onRowSelect = () => {},
  draggableRows = false,
  getDraggableData = (row) => row,
}) => {
  useLanguageChange(data.t);
  const { sortedData: hookSortedData, sortConfig: hookSortConfig, handleSort: hookHandleSort } = useSortableTableData(tableData);
  const sortedData = sortable ? hookSortedData : tableData;
  const sortConfig = sortable ? hookSortConfig : {};
  const handleSort = sortable ? hookHandleSort : () => {};

  const [search, setSearch] = useState('');
  // Track selected rows by a stable id (id | uuid | _id | original index)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const isUpdatingFromPropRef = useRef(false);

  const columnKeys = typeof columnNames === 'object' && !Array.isArray(columnNames) ? Object.keys(columnNames) : columnNames;

  const filteredData = useTableSearch({
    data: tableData,
    columnNames: columnKeys,
    search,
    sortedData,
    sortConfig,
  });

  // Helper to get a stable row id
  const getRowId = (row) => row?.id ?? row?.uuid ?? row?._id ?? tableData.indexOf(row);

  const isControlled = selectedRows !== null;

  // Sync external selectedRows prop with internal state (controlled mode)
  useEffect(() => {
    if (!isControlled) return;
    isUpdatingFromPropRef.current = true;
    const newSelectedIds = new Set(selectedRows.map((r) => getRowId(r)));
    setSelectedIds(newSelectedIds);
    // Reset flag after state update completes
    setTimeout(() => {
      isUpdatingFromPropRef.current = false;
    }, 0);
  }, [selectedRows, isControlled]);

  // Clean up invalid selections when tableData changes (uncontrolled mode)
  useEffect(() => {
    if (isControlled) return;
    setSelectedIds((prev) => {
      const validIds = new Set(tableData.map((r) => getRowId(r)));
      let removed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          removed = true;
        }
      });
      return removed ? next : prev;
    });
  }, [tableData, isControlled]);

  // Select/Deselect a single row
  const toggleRowSelection = (row) => {
    const id = getRowId(row);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select/Deselect all currently visible (filtered) rows
  const allVisibleIds = useMemo(() => new Set(filteredData.map((r) => getRowId(r))), [filteredData]);
  const areAllVisibleSelected = allVisibleIds.size > 0 && [...allVisibleIds].every((id) => selectedIds.has(id));

  const toggleSelectAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        allVisibleIds.forEach((id) => next.add(id));
      } else {
        allVisibleIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  // Notify parent AFTER selection state updates commit to avoid setState during render warnings
  useEffect(() => {
    if (!selectableRows || isUpdatingFromPropRef.current) return;
    const selectedRowsData = tableData.filter((r) => selectedIds.has(getRowId(r)));
    onRowSelect(selectedRowsData);
  }, [selectedIds, tableData, selectableRows]);

  return (
    <div className='custom-table-container m-0 p-0' data-testid='table-container'>
      {searchable && (
        <div className='search-container' data-testid='search-container'>
          <div className='search-icon'>
            <FontAwesomeIcon icon={faSearch} />
          </div>
          <input type='text' placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} data-testid='search-input' />
          {search.length > 0 && (
            <button className='clear-icon' onClick={() => setSearch('')} aria-label='Clear search' data-testid='clear-search-button'>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      )}
      <div className='table-wrapper'>
        <table className='custom-table m-0 p-0 table-borderless' data-testid='table'>
          <thead data-testid='table-header'>
            <tr>
              {selectableRows && (
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <Checkbox
                    isChecked={areAllVisibleSelected}
                    onChange={(checked) => toggleSelectAllVisible(checked)}
                    data-testid='select-all-checkbox'
                  />
                </th>
              )}
              {columnKeys.map((key, index) => (
                <th
                  key={index}
                  onClick={sortable ? () => handleSort(key) : undefined}
                  style={{ cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}
                  data-testid={`column-header-${key}`}
                >
                  {data.t.read(columnNames[key])}
                  {sortable && sortConfig.key === key && sortConfig.direction && (
                    <span>
                      <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faArrowUp : faArrowDown} size='sm' />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid='table-body'>
            {isLoading ? (
              <tr className='loading' data-testid='loading-row'>
                <td className='loading-cell' colSpan={(selectableRows ? 1 : 0) + columnKeys.length}>
                  <div className='loading-abs-center'>
                    <LoadingSpinner data-testid='table-loading-spinner' />
                  </div>
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((row, rowIndex) => {
                const id = getRowId(row);
                const checked = selectedIds.has(id);
                const rowContent = (
                  <>
                    {selectableRows && (
                      <td>
                        {selectableRowsCondition(row) && (
                          <Checkbox isChecked={checked} onChange={() => toggleRowSelection(row)} data-testid={`row-checkbox-${rowIndex}`} />
                        )}
                      </td>
                    )}
                    {columnKeys.map((colKey, colIndex) => (
                      <td
                        key={colIndex}
                        onClick={() => onRowClick(row)}
                        className={onRowClick !== (() => {}) ? 'clickable-row' : ''}
                        data-testid={`cell-${rowIndex}-${colKey}`}
                      >
                        {row[colKey]}
                      </td>
                    ))}
                  </>
                );

                if (draggableRows) {
                  return (
                    <DraggableRow key={id} rowData={getDraggableData(row)} data-testid={`table-row-${rowIndex}`}>
                      {rowContent}
                    </DraggableRow>
                  );
                }

                return (
                  <DroppableRow
                    key={id}
                    transaction={row}
                    onExpenseDrop={(expense, transaction) => onExpenseDrop(expense, transaction)}
                    data-testid={`table-row-${rowIndex}`}
                  >
                    {rowContent}
                  </DroppableRow>
                );
              })
            ) : (
              <tr data-testid='no-data-row'>
                <td colSpan={(selectableRows ? 1 : 0) + columnKeys.length}>{data.t.read('no_data')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.shape({
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  columnNames: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  tableData: PropTypes.array.isRequired,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  sortable: PropTypes.bool,
  onRowClick: PropTypes.func,
  onExpenseDrop: PropTypes.func,
  selectableRows: PropTypes.bool,
  onRowSelect: PropTypes.func,
  draggableRows: PropTypes.bool,
  getDraggableData: PropTypes.func,
};

export default inject('data')(Table);
